import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auditService, AuditLog, CreateAuditLogData } from '../services/audit';

// Hook pour récupérer les logs d'audit
export const useAuditLogs = (filters?: {
  actorId?: string;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  startDate?: string;
  endDate?: string;
  scope?: string;
}) => {
  return useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: () => auditService.getLogs(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook pour récupérer l'historique d'une ressource
export const useResourceHistory = (resourceType: string, resourceId: string) => {
  return useQuery({
    queryKey: ['audit-logs', 'resource', resourceType, resourceId],
    queryFn: () => auditService.getResourceHistory(resourceType, resourceId),
    enabled: !!resourceType && !!resourceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook pour créer un log d'audit
export const useCreateAuditLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAuditLogData) => auditService.createLog(data),
    onSuccess: () => {
      // Invalider les caches des logs d'audit
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
    },
  });
};

// Hook pour logger les actions courantes
export const useAuditActions = () => {
  const createLogMutation = useCreateAuditLog();

  const logCreate = async (
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    newValue: any,
    scope?: string
  ) => {
    try {
      await auditService.logCreate(resourceType, resourceId, newValue, scope);
    } catch (error) {
      console.error('Erreur lors du logging de création:', error);
    }
  };

  const logUpdate = async (
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    previousValue: any,
    newValue: any,
    scope?: string
  ) => {
    try {
      await auditService.logUpdate(resourceType, resourceId, previousValue, newValue, scope);
    } catch (error) {
      console.error('Erreur lors du logging de modification:', error);
    }
  };

  const logDelete = async (
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    previousValue: any,
    scope?: string
  ) => {
    try {
      await auditService.logDelete(resourceType, resourceId, previousValue, scope);
    } catch (error) {
      console.error('Erreur lors du logging de suppression:', error);
    }
  };

  const logArchive = async (
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    previousValue: any,
    scope?: string
  ) => {
    try {
      await auditService.logArchive(resourceType, resourceId, previousValue, scope);
    } catch (error) {
      console.error('Erreur lors du logging d\'archivage:', error);
    }
  };

  const logRestore = async (
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    newValue: any,
    scope?: string
  ) => {
    try {
      await auditService.logRestore(resourceType, resourceId, newValue, scope);
    } catch (error) {
      console.error('Erreur lors du logging de restauration:', error);
    }
  };

  const logMove = async (
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    previousValue: any,
    newValue: any,
    scope?: string
  ) => {
    try {
      await auditService.logMove(resourceType, resourceId, previousValue, newValue, scope);
    } catch (error) {
      console.error('Erreur lors du logging de déplacement:', error);
    }
  };

  const logExport = async (
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    scope?: string
  ) => {
    try {
      await auditService.logExport(resourceType, resourceId, scope);
    } catch (error) {
      console.error('Erreur lors du logging d\'export:', error);
    }
  };

  const logImport = async (
    resourceType: AuditLog['resourceType'],
    resourceId: string,
    newValue: any,
    scope?: string
  ) => {
    try {
      await auditService.logImport(resourceType, resourceId, newValue, scope);
    } catch (error) {
      console.error('Erreur lors du logging d\'import:', error);
    }
  };

  return {
    logCreate,
    logUpdate,
    logDelete,
    logArchive,
    logRestore,
    logMove,
    logExport,
    logImport,
    isLoading: createLogMutation.isPending,
    error: createLogMutation.error,
  };
};

export default useAuditActions;
